/**
 * Le mode démo — l'interrupteur, et rien d'autre.
 *
 * ON, le cockpit reste EXACTEMENT le cockpit : mêmes écrans, mêmes créatrices,
 * même session. Seule la surface `/api/conversations*` est servie par le monde
 * de démonstration (`serveur.ts`) au lieu de Cyrano. Tout le reste — le Vault,
 * le catalogue d'offres, les créatrices, La Régie — continue de parler à la
 * prod, en lecture comme d'habitude.
 *
 * ⛔ **Aucun message ne peut partir sur Telegram tant que ce drapeau est levé.**
 * C'est la propriété qui autorise à filmer : `apiRequest` détourne TOUT
 * `/api/conversations*`, jamais seulement ce que le monde connaît. Un chemin
 * inconnu rend une réponse vide plutôt que d'aller au réseau — voir
 * `serveur.ts`, qui porte cette garde.
 *
 * L'état vit dans localStorage sous UNE clé, lue de deux façons :
 *   - `modeDemoAtom` (jotai) pour le rendu React,
 *   - `estEnDemo()` pour les couches sans React (le client HTTP, le flux SSE).
 * Une seule clé, deux lecteurs : rien à synchroniser.
 *
 * 🔐 **Mais le drapeau ne suffit pas : le COMPTE décide.** Kayla, 19-08-2026 :
 * « l'interrupteur, c'est bon que s'il y a que Mayumi qui l'a ». Un drapeau
 * localStorage est par NAVIGATEUR, pas par compte — sur un poste partagé entre
 * deux comptes, il suivrait les deux. `estEnDemo()` exige donc les DEUX : le
 * drapeau levé ET une session ouverte sur un compte de démonstration. Un autre
 * compte peut poser le drapeau (par `?demo=1`, ou par un vieux localStorage) :
 * il ne verra rien de fictif pour autant.
 */
import { useAtom, useAtomValue } from "jotai";
import { useSyncExternalStore } from "react";
import { atomWithStorage } from "jotai/utils";

export const CLE_MODE_DEMO = "cockpit:mode-demo:v1";

/**
 * Les comptes qui ont le droit d'armer la démo, par `workosUserId`.
 *
 * Un seul aujourd'hui : le compte de tournage de Kayla (pseudo cockpit
 * « mayumi lpb »). Il est nommé ici et pas dans une table Convex à dessein :
 * « qui peut passer en démo ? » est une question dont la réponse doit se lire
 * dans le dépôt, pas se deviner en interrogeant la prod. Le jour où le compte
 * de tournage change, c'est cette ligne qu'on change — et la panne est bénigne
 * et parlante : la case disparaît des Réglages.
 */
/* Exporté POUR SON TEST : celui-ci vérifie la règle sans recopier
 * l'identifiant, qui n'aurait alors qu'à diverger. */
export const COMPTES_DE_DEMO = new Set<string>([
  "user_01KRC55BMVCXFNB46P25KBFXR3",
]);

/**
 * Le `workosUserId` de la session ouverte, tenu à jour par `SessionProvider`.
 *
 * 🪤 Pourquoi une variable de module et pas un hook : `estEnDemo()` est appelée
 * depuis le client HTTP et le flux SSE, qui ne sont pas des composants. Elle est
 * posée de façon SYNCHRONE au moment où la session se résout — et toutes les
 * requêtes de conversation sont gardées par `enabled: isAuthenticated`, donc
 * aucune ne peut partir avant. Il n'y a pas de fenêtre où l'on ne saurait pas
 * encore qui regarde.
 */
let utilisateurCourant: string | null = null;
const abonnes = new Set<() => void>();

/** Appelé par `SessionProvider` à chaque résolution de session (et à la
 *  déconnexion, avec `null`). */
export function poserLutilisateurCourant(workosUserId: string | null): void {
  if (workosUserId === utilisateurCourant) return;
  utilisateurCourant = workosUserId;
  for (const prevenir of [...abonnes]) prevenir();
}

/**
 * L'identité courante, côté React.
 *
 * 🪤 Un `useSession()` aurait été plus direct — et il LÈVE hors de son
 * provider. Les écrans du shift se testent unitairement sans provider de
 * session (`ShiftGateOverlay.render.test.tsx`) : y brancher la session ferait
 * tomber sept tests qui ne parlent pas de démo du tout. Un abonnement maison
 * rend `null` dans ce cas — « je ne sais pas qui regarde », donc pas de démo,
 * ce qui est exactement le bon défaut.
 */
function useUtilisateurCourant(): string | null {
  return useSyncExternalStore(
    (prevenir) => {
      abonnes.add(prevenir);
      return () => {
        abonnes.delete(prevenir);
      };
    },
    () => utilisateurCourant,
    () => utilisateurCourant,
  );
}

/**
 * Le mode est-il actif POUR DE VRAI ? Drapeau levé **et** session ouverte sur
 * un compte de démonstration. C'est la seule question que doivent poser les
 * écrans : un drapeau qui traîne dans le localStorage d'un poste partagé ne
 * doit rien fabriquer de fictif chez quelqu'un qui travaille.
 */
export function useModeDemo(): boolean {
  const drapeau = useAtomValue(modeDemoAtom);
  const qui = useUtilisateurCourant();
  return drapeau && estUnCompteDeDemo(qui);
}

/**
 * Ce compte a-t-il le droit d'armer la démo ? Décide si le réglage EXISTE —
 * pour tout le monde d'autre il n'y a pas de case, donc pas de question à se
 * poser un jour de shift.
 */
export function useCompteDeDemo(): boolean {
  return estUnCompteDeDemo(useUtilisateurCourant());
}

/** Ce compte a-t-il le droit d'armer la démo ? */
export function estUnCompteDeDemo(workosUserId: string | null | undefined): boolean {
  return workosUserId ? COMPTES_DE_DEMO.has(workosUserId) : false;
}

/** Le drapeau, côté React. `atomWithStorage` écrit du JSON (`true`/`false`). */
export const modeDemoAtom = atomWithStorage<boolean>(CLE_MODE_DEMO, false);

/**
 * Le drapeau, hors React. Relit localStorage à chaque appel — pas de cache :
 * un cache raterait le `?demo=1` posé avant le premier render, et le coût
 * (une lecture synchrone par requête) est nul devant un fetch.
 */
export function estEnDemo(): boolean {
  return drapeauLeve() && estUnCompteDeDemo(utilisateurCourant);
}

/** Le drapeau seul — SANS la question du compte. Réservé au réglage lui-même
 *  (une case doit refléter ce qu'on a coché, pas ce qui est effectif). */
export function drapeauLeve(): boolean {
  try {
    return window.localStorage.getItem(CLE_MODE_DEMO) === "true";
  } catch {
    // localStorage indisponible (navigation privée stricte, SSR de test) —
    // l'absence d'information n'est pas « oui ».
    return false;
  }
}

/** Lecture + écriture (le réglage dans Préférences). */
export function useModeDemoToggle(): [boolean, (v: boolean) => void] {
  const [valeur, poser] = useAtom(modeDemoAtom);
  return [valeur, poser];
}

/** Pose le drapeau sans passer par React (le paramètre d'URL, le réglage). */
export function poserLeModeDemo(actif: boolean): void {
  try {
    window.localStorage.setItem(CLE_MODE_DEMO, actif ? "true" : "false");
  } catch {
    /* pas de stockage : le mode ne survivra pas au rechargement, tant pis */
  }
}

/**
 * `?demo=1` allume, `?demo=0` éteint — et le paramètre est retiré de la barre
 * d'adresse dans la foulée.
 *
 * 🔐 Il ne contourne RIEN : il ne fait que lever le drapeau. Sur un compte qui
 * n'est pas un compte de démonstration, le drapeau levé ne produit aucun effet
 * (cf `estEnDemo`). C'est une commodité de tournage, pas une porte.
 *
 * À appeler AVANT le premier render (cf `main.tsx`) : les premières requêtes
 * partent dans la foulée, et elles doivent déjà savoir.
 */
export function lireLeParametreDurl(): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const valeur = params.get("demo");
  if (valeur === null) return;
  poserLeModeDemo(valeur === "1" || valeur === "true" || valeur === "on");
  params.delete("demo");
  const query = params.toString();
  const url =
    window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
  window.history.replaceState(null, "", url);
}
